import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MoldType } from '../../../../api/elite/types/mold-type';
import { MoldRevisionItem } from '../../models/item';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MoldDialogFormService } from '../form/form.service';
import { Mold } from '../../../../api/elite/types/mold';
import { AssemblyItem } from 'src/app/api/elite/types/assembly-item';
import { MoldAuthService } from '../../services/auth.service';
import { ApiRouteAssemblyItemService } from 'src/app/api/elite/routes/assembly.service';

export type MoldDialogCreateNamesForm =
  | 'name'
  | 'cavities'
  | 'cyclesToRepair'
  | 'cyclesToObsolete'
  | 'searchMold'
  | 'newMold'
  | 'serialized'
  | 'moldType'
  | 'assembly'
  | 'image'
  | 'imageName';
@Injectable()
export class MoldDialogCreateMoldService implements OnDestroy {
  form = new FormGroup({
    name: new FormControl<string>(undefined, Validators.required),
    cavities: new FormControl<number>(
      { value: undefined, disabled: true },
      Validators.required
    ),
    cyclesToRepair: new FormControl<number>(
      { value: undefined, disabled: true },
      Validators.required
    ),
    cyclesToObsolete: new FormControl<number>(
      { value: undefined, disabled: true },
      Validators.required
    ),
    searchMold: new FormControl<string>(undefined),
    newMold: new FormControl<boolean>(false),
    moldType: new FormControl<number>({ value: undefined, disabled: true }),
    assembly: new FormControl<string | { id: number; title: string } | null>({
      value: null,
      disabled: true,
    }),
    serialized: new FormControl<boolean>({
      value: false,
      disabled: true,
    }),
    image: new FormControl<File | null>({ value: null, disabled: true }),
    imageName: new FormControl<string | null>({ value: null, disabled: true }),
  });
  onEdit = false;
  private readonly apiRouteAssemblyItemService = inject(
    ApiRouteAssemblyItemService
  );
  private allAssembly: any[] = [];
  private filteredAsemblySubject = new BehaviorSubject<any[]>([]);
  filteredAssembly$ = this.filteredAsemblySubject.asObservable();
  private listMoldTypes: MoldType[] = [];
  get moldTypes(): MoldType[] {
    return this.listMoldTypes;
  }
  set moldTypes(val: MoldType[]) {
    this.listMoldTypes = val;
  }
  moldsFiltered$ = this.form.get('searchMold').valueChanges.pipe(
    startWith(''),
    map((search) => {
      const q = (search ?? '').toLowerCase();
      return this.formService.molds.filter((m) =>
        m.name.toLowerCase().includes(q)
      );
    })
  );

  private _showControls = new BehaviorSubject<{
    [key in MoldDialogCreateNamesForm]?: boolean;
  }>({
    cavities: true,
    newMold: true,
    moldType: true,
    assembly: false,
    serialized: true,
  });
  showControls$ = this._showControls.asObservable();

  showStepSelectionBasePlates$ = this.form.get('moldType').valueChanges.pipe(
    startWith(0),
    map((val) => this.listMoldTypes.find((t) => t.id === val)?.title === 'Mold')
  );

  private subscription: Subscription;

  private customInputEnabled: MoldDialogCreateNamesForm[] = [];

  constructor(
    private readonly formService: MoldDialogFormService,
    private readonly authService: MoldAuthService
  ) {
    this.subscription = this.form
      .get('name')
      .valueChanges.pipe(startWith(''))
      .subscribe((filter) => {
        this.onChangeFormName(filter);
      });
    this.subscription.add(
      combineLatest([
        this.form.get('newMold').valueChanges.pipe(startWith(false)),
        this.formService.selectedMold$,
      ]).subscribe(([val, mold]) => {
        this.onChangeNewMold(val, mold);
      })
    );
    this.subscription.add(
      this.form
        .get('moldType')
        .valueChanges.subscribe((val) => this.onChangeMoldType(val))
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  setMoldRevision(
    rev: MoldRevisionItem,
    type: 'edit' | 'create',
    enableControls?: MoldDialogCreateNamesForm[]
  ): void {
    this.form.patchValue(
      {
        name: rev.mold.name,
        cavities: rev.mold.details.cavities,
        cyclesToRepair: rev.mold.cyclesForRepair,
        cyclesToObsolete: rev.mold.cyclesToObsolete,
        searchMold: '',
        newMold: false,
        moldType: rev.mold.details.typeId,
        serialized: rev.mold.details.serialized,
        imageName: rev.mold.details.resourceFiles?.name ?? null,
        image: null,
      },
      { emitEvent: false }
    );

    if (this.form.get('assembly')?.enabled) {
      this.form
        .get('assembly')
        ?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    if (enableControls?.length) {
      this.enableCustomControls(enableControls);
    }
    if (type === 'edit') {
      this.caseOfEdit(rev);
    }
  }
  setAssemblyList(data: AssemblyItem[]): void {
    this.allAssembly = data ?? [];
    this.filteredAsemblySubject.next(this.allAssembly);
    if (this.form.contains('assembly')) {
      this.form
        .get('assembly')
        ?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private caseOfEdit(mold: MoldRevisionItem): void {
    this.onEdit = true;
    if (!this.authService.editName()) {
      this.form.get('name').disable();
    }
    const controls: MoldDialogCreateNamesForm[] = this.customInputEnabled.length
      ? this.customInputEnabled
      : [
          'cavities',
          'cyclesToRepair',
          'cyclesToObsolete',
          'moldType',
          'serialized',
          'image',
          'imageName',
        ];
    this.form.get('newMold').disable();
    this.form.get('searchMold').disable();
    this.enableControls([...controls]);
    if (mold.typeOfMold === 'Base Plate') {
      this.form.get('cavities').disable();
      this.form.get('cavities').clearValidators();
      this._showControls.next({
        ...this._showControls.value,
        cavities: false,
      });
    }
  }

  private onChangeFormName(filter: string): void {
    const title = filter?.toUpperCase().trim();
    const mold = this.formService.molds.find(
      (m) => m.name?.toUpperCase() === title
    );
    this.formService.selectedMold = mold;
    if (mold) {
      this.form.get('cavities').setValue(mold?.details?.cavities);
      this.form.get('cyclesToRepair').setValue(mold.cyclesForRepair);
      this.form.get('cyclesToObsolete').setValue(mold.cyclesToObsolete);
      this.form.get('moldType').setValue(mold.details?.typeId);
      this.form.get('assembly').setValue(mold.assemblyItem.title);
      this.form.get('serialized').setValue(mold.details.serialized);
      this.form.get('image').setValue(null);
      this.form
        .get('imageName')
        .setValue(mold.details.resourceFiles?.name ?? null);

      this.disableControls([
        'cavities',
        'cyclesToRepair',
        'cyclesToObsolete',
        'moldType',
        'assembly',
        'image',
        'imageName',
      ]);
    } else {
      this.enableControls([
        'cavities',
        'cyclesToRepair',
        'cyclesToObsolete',
        'moldType',
        'assembly',
        'image',
        'imageName',
      ]);
    }
  }

  private onChangeNewMold(val: boolean, moldSelected: Mold): void {
    this.form.get('searchMold').setValue('');
    if (val && !moldSelected) {
      this.enableControls([
        'cavities',
        'cyclesToRepair',
        'cyclesToObsolete',
        'moldType',
        'assembly',
        'serialized',
        'image',
        'imageName',
      ]);
    } else {
      this.disableControls([
        'cavities',
        'serialized',
        'cyclesToRepair',
        'cyclesToObsolete',
        'moldType',
        'assembly',
        'image',
        'imageName',
      ]);
    }
  }

  private onChangeMoldType(val: number): void {
    const selected = this.listMoldTypes.find((t) => t.id === val);
    if (selected?.title === 'Base Plate') {
      this.removeValidators('cavities');
      this.form.get('cavities').setValue(undefined);
      this._showControls.next({
        ...this._showControls.value,
        cavities: false,
      });
    } else if (selected?.title === 'Mold') {
      this.addValidators('cavities');
      this._showControls.next({
        ...this._showControls.value,
        cavities: true,
      });
    }
  }

  private enableControls(
    name: MoldDialogCreateNamesForm | MoldDialogCreateNamesForm[]
  ): void {
    if (Array.isArray(name)) {
      name.forEach((n) => this.form.get(n).enable());
    } else {
      this.form.get(name).enable();
    }
  }
  private disableControls(
    name: MoldDialogCreateNamesForm | MoldDialogCreateNamesForm[]
  ): void {
    if (Array.isArray(name)) {
      name.forEach((n) => this.form.get(n).disable());
    } else {
      this.form.get(name).disable();
    }
  }

  enableCustomControls(names: MoldDialogCreateNamesForm[]): void {
    if (!names.length) {
      return;
    }
    this.customInputEnabled = names;
    Object.entries(this.form.controls).forEach(([key, control]) => {
      if (names.includes(key as MoldDialogCreateNamesForm)) {
        control.enable();
      } else {
        control.disable();
      }
    });
  }

  addValidators(name: MoldDialogCreateNamesForm): void {
    this.form.get(name).setValidators(Validators.required);
  }
  removeValidators(name: MoldDialogCreateNamesForm): void {
    this.form.get(name).clearValidators();
  }
  searchOptions(query: string): void {
    if (query) {
      const filtered = this.allAssembly.filter((option) =>
        option.title.toLowerCase().includes(query.toLowerCase())
      );
      this.filteredAsemblySubject.next(filtered);
    } else {
      this.filteredAsemblySubject.next(this.allAssembly);
    }
  }
  validAssemblyOption(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return { required: true };
      }

      const options = this.allAssembly || [];
      if (!options.length) {
        return { invalidAssembly: true };
      }

      if (typeof value === 'object' && value?.id != null) {
        const ok = options.some((o) => o.id === value.id);
        return ok ? null : { invalidAssembly: true };
      }

      if (typeof value === 'string') {
        const v = value.toLowerCase().trim();
        const ok = options.some((o) => o.title?.toLowerCase().trim() === v);
        return ok ? null : { invalidAssembly: true };
      }

      return { invalidAssembly: true };
    };
  }
  chargeDepartmentData(
    department: string,
    type: 'create' | 'edit',
    rev?: MoldRevisionItem
  ): void {
    const assemblyCtrl = this.form.get('assembly');

    if (department === 'Plastics') {
      this._showControls.next({ ...this._showControls.value, assembly: false });
      assemblyCtrl?.disable({ emitEvent: false });

      const initialValue: string | null =
        rev?.mold?.assemblyItem?.title ?? null;

      assemblyCtrl?.setValue(initialValue, { emitEvent: false });

      this.apiRouteAssemblyItemService
        .getAssemblyDepartment(14)
        .subscribe((data) => {
          this.setAssemblyList(data);

          assemblyCtrl?.setValidators([
            Validators.required,
            this.validAssemblyOption(),
          ]);
          assemblyCtrl?.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
          });

          this._showControls.next({
            ...this._showControls.value,
            assembly: true,
          });
          if (
            type === 'edit' &&
            (this.customInputEnabled.length === 0 ||
              this.customInputEnabled.includes('assembly'))
          ) {
            assemblyCtrl?.enable({ emitEvent: false });
          }
        });
    } else {
      assemblyCtrl?.clearValidators();
      assemblyCtrl?.reset(null, { emitEvent: false });
      assemblyCtrl?.disable({ emitEvent: false });
      assemblyCtrl?.updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });

      this._showControls.next({ ...this._showControls.value, assembly: false });
      this.allAssembly = [];
      this.filteredAsemblySubject.next([]);
    }
  }
  setImage(file: File): void {
    this.form.patchValue({
      image: file,
      imageName: file.name,
    });
  }

  clearImage(): void {
    this.form.patchValue({
      image: null,
      imageName: null,
    });
  }
  get imageFile(): File | null {
    return this.form.get('image')?.value ?? null;
  }

  get imageFileName(): string | null {
    return this.form.get('imageName')?.value ?? null;
  }
}
